/**
 * The JS side of the spam-score contract (`src-tauri/src/mail_catcher/spam.rs`).
 *
 * Rust owns the rules and the numbers; this file owns the CATALOGUE of rule
 * ids (for the modal's copy lookup) and the pure presentation maths. The rule
 * ids and `SPAM_THRESHOLD` are pinned to the Rust source by
 * `src/tests/mailCatcherSpamRules.test.js`, which scans `spam.rs` — this list
 * is the thing under test, never the oracle. Copy per rule lives in the
 * locale bundles as `mailCatcher.spam.rules.<ID>.{name,desc,hint}`.
 *
 * Pure, no React, no Tauri.
 */

/** Same literal as `pub const SPAM_THRESHOLD: f64 = 5.0;` in spam.rs. */
export const SPAM_THRESHOLD = 5;

/** The 26 v1 rule ids, in Rust table order. */
export const KNOWN_RULE_IDS = [
  'MISSING_MESSAGE_ID',
  'INVALID_MESSAGE_ID',
  'MISSING_DATE',
  'MISSING_FROM',
  'FROM_ENVELOPE_MISMATCH',
  'REPLY_TO_MISMATCH',
  'MISSING_TO',
  'MANY_RECIPIENTS',
  'PRIORITY_SHOUTING',
  'BULK_NO_UNSUBSCRIBE',
  'SUBJECT_MISSING',
  'SUBJECT_ALL_CAPS',
  'SUBJECT_EXCESSIVE_PUNCT',
  'SUBJECT_FAKE_REPLY',
  'HTML_ONLY',
  'HTML_IMAGE_ONLY',
  'EMPTY_BODY',
  'TRACKING_PIXEL',
  'SPAMMY_PHRASES',
  'URL_IP_LITERAL',
  'URL_SHORTENER',
  'LINK_TEXT_MISMATCH',
  'MANY_LINKS',
  'EXECUTABLE_ATTACHMENT',
  'BODY_SHOUTING',
  'BODY_EXCESSIVE_EXCLAMATION',
];

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * `low` below 40 % of the threshold, `mid` from there up to (not including)
 * the threshold, `high` at or above it (PLAN D5). With the default 5.0:
 * low < 2.0, mid 2.0–4.99, high ≥ 5.0.
 */
export function spamBand(score, threshold = SPAM_THRESHOLD) {
  const s = num(score);
  const t = num(threshold) || SPAM_THRESHOLD;
  if (s >= t) return 'high';
  if (s >= 0.4 * t) return 'mid';
  return 'low';
}

/** Bars lit on the 5-bar meter: `min(5, ceil(score / threshold * 5))`, 0 for 0. */
export function meterBars(score, threshold = SPAM_THRESHOLD) {
  const s = num(score);
  const t = num(threshold) || SPAM_THRESHOLD;
  if (s <= 0) return 0;
  return Math.min(5, Math.ceil((s * 5) / t));
}

/** One decimal, always — "0.0", "5.0", "12.3". */
export function formatScore(score) {
  return num(score).toFixed(1);
}

/** The three locale keys for one rule id. */
export function ruleKeys(id) {
  const base = `mailCatcher.spam.rules.${id}`;
  return { name: `${base}.name`, desc: `${base}.desc`, hint: `${base}.hint` };
}

/**
 * `detail.spam` when the backend sent one; otherwise a report built from the
 * summary's score with no rules (older fixtures / a detail without `spam`).
 * Never throws on a null detail.
 */
export function normalizeSpam(detail) {
  const spam = detail && detail.spam;
  if (spam && typeof spam === 'object') {
    return {
      score: num(spam.score),
      threshold: num(spam.threshold) || SPAM_THRESHOLD,
      rules: Array.isArray(spam.rules) ? spam.rules : [],
    };
  }
  return {
    score: num(detail && detail.summary && detail.summary.spamScore),
    threshold: SPAM_THRESHOLD,
    rules: [],
  };
}
